import React, { useEffect, useState } from 'react';

import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';

// Third Party Libraries
import DatePicker from '~/components/General/OlcDatePicker';

// Hooks
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

// Actions
import {
  searchAndAdvanceChange,
  fetchOrderContactDetails,
} from '~/redux/actions/orders-actions.js';
import { failure } from '~/redux/actions/snackbar-actions';

// Utils
import { MESSAGES } from '~/utils/message.js';
import { DATE_REGEX, DEFAULT_PAGINATION } from '~/utils/constants';
import { handleRawChangeForDatePicker } from '~/utils/date-format';

// Mui Components
import { Box, Checkbox, FormControlLabel, InputLabel, Typography } from '@mui/material';

// Components
import MultiSelect from '~/components/General/MultiSelectNew';

// Icons
import { CheckedCIcon, MultiSelectArrowCIcon, UncheckedCIcon } from '~/assets/images';

// Styles
import './styles.scss';

const address = [
  {
    id: 1,
    title: 'Verified',
  },
  {
    id: 2,
    title: 'Corrected',
  },
  {
    id: 3,
    title: 'Failed',
  },
];
const status = [
  {
    id: 1,
    title: 'Not Mailed',
  },
  {
    id: 2,
    title: 'Mailed',
  },
  {
    id: 3,
    title: 'Scanned',
  },
  {
    id: 4,
    title: 'Not Scanned',
  },
  {
    id: 5,
    title: 'Early',
  },
  {
    id: 6,
    title: 'On Time',
  },
  {
    id: 7,
    title: 'Late',
  },
  {
    id: 8,
    title: 'En Route',
  },
  {
    id: 9,
    title: 'Delivered',
  },
  {
    id: 10,
    title: 'Re-Routed',
  },
  {
    id: 11,
    title: 'Returned to Sender',
  },
];

const FilterModal = ({ handleClose, open, search }) => {
  const [addressStatus, setAddressStatus] = useState([]);
  const [mailedStatus, setMailedStatus] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [isDatepickerOpen, setIsDatepickerOpen] = useState(false);
  const [noDeliveryDate, setNoDeliveryDate] = useState(false);

  const contactsPagination = useSelector(
    (state) => state.ordersReducers.detailContacts.contactsPagination
  );

  const appliedFilters = useSelector((state) => state.ordersReducers.detailContacts.filters);

  const { id } = useParams();
  const dispatch = useDispatch();

  const handleSearch = () => {
    if (
      addressStatus.length === 0 &&
      mailedStatus.length === 0 &&
      (startDate === null || endDate === null) &&
      !noDeliveryDate
    ) {
      dispatch(failure(MESSAGES.ORDERS.FILTERS_REQUIRED));
      return;
    }
    let filters = {};
    addressStatus.length > 0 && (filters.addressStatus = addressStatus);
    mailedStatus.length > 0 && (filters.mailedStatus = mailedStatus);
    noDeliveryDate
      ? (filters.noDeliveryDate = true)
      : startDate !== null && endDate !== null && (filters.deliveredDate = { startDate, endDate });

    dispatch(searchAndAdvanceChange('filters', filters));
    dispatch(
      fetchOrderContactDetails({
        id,
        pagination: { ...contactsPagination, page: DEFAULT_PAGINATION.page },
        filters: filters,
        applyFilters: true,
        search,
      })
    );
    handleClose();
  };

  const handleClear = () => {
    dispatch(searchAndAdvanceChange('filters', {}));
    dispatch(
      fetchOrderContactDetails({
        id,
        pagination: { ...contactsPagination, page: DEFAULT_PAGINATION.page },
        search,
      })
    );
    handleClose();
  };

  useEffect(() => {
    if (Object.keys(appliedFilters).length > 0) {
      setAddressStatus(appliedFilters.addressStatus || []);
      setMailedStatus(appliedFilters.mailedStatus || []);
      appliedFilters.deliveredDate &&
        setDateRange([
          appliedFilters.deliveredDate.startDate,
          appliedFilters.deliveredDate.endDate,
        ]);
      appliedFilters.noDeliveryDate && setNoDeliveryDate(true);
    }
  }, []);

  return (
    <OlcFormModal
      open={open}
      onClose={handleClose}
      title={MESSAGES.ORDERS.DETAIL.CONTACTS.FILTER.TITLE}
      className="contactfilterModalWrapper"
      footer={
        <>
          <OlcButton variant="secondary" onClick={handleClear}>
            {MESSAGES.ORDERS.DETAIL.CONTACTS.FILTER.CANCEL_BUTTON}
          </OlcButton>
          <OlcButton variant="primary" autoFocus onClick={handleSearch}>
            {MESSAGES.ORDERS.DETAIL.CONTACTS.FILTER.SUBMIT_BUTTON}
          </OlcButton>
        </>
      }
    >
      <Box className="inputWrapper">
        <Typography className="filterModalLabel">
          {MESSAGES.ORDERS.DETAIL.CONTACTS.FILTER.ADDRESS_STATUS}
        </Typography>
        <MultiSelect
          options={address}
          selectedValue={addressStatus}
          setSelectedValue={setAddressStatus}
          placeHolderText={MESSAGES.ORDERS.DETAIL.CONTACTS.FILTER.ADDRESS_STATUS}
          clearable={true}
          noOptionText="No Address Status to show"
          newDesign={true}
        />
      </Box>
      <Box className="inputWrapper">
        <Typography className="filterModalLabel">
          {MESSAGES.ORDERS.DETAIL.CONTACTS.FILTER.MAILED_STATUS}
        </Typography>
        <MultiSelect
          options={status}
          selectedValue={mailedStatus}
          setSelectedValue={setMailedStatus}
          placeHolderText={MESSAGES.ORDERS.DETAIL.CONTACTS.FILTER.MAILED_STATUS}
          clearable={true}
          noOptionText="No Mailed Status to show"
          newDesign={true}
        />
      </Box>
      <Box className="mailedDate updatedDesign newDatePicker noDeliveryDate">
        <Box className="selectDate">
          <InputLabel
            className="dateLabel deliveredLabel"
            sx={{ opacity: noDeliveryDate ? '0.5' : '1' }}
          >
            {MESSAGES.ORDERS.DETAIL.CONTACTS.FILTER.DELIVERED_DATE}
          </InputLabel>
          <img
            src={MultiSelectArrowCIcon}
            alt="arrow"
            style={{
              transform: isDatepickerOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
          <DatePicker
            selectsRange={true}
            startDate={startDate}
            endDate={endDate}
            onChange={(update) => {
              setDateRange(update);
            }}
            onChangeRaw={(e) => {
              if (e.target.value === '' || DATE_REGEX.test(e.target.value)) {
                handleRawChangeForDatePicker(e, setDateRange);
              } else {
                e.preventDefault();
              }
            }}
            onFocus={() => !noDeliveryDate && setIsDatepickerOpen(true)}
            onBlur={() => setIsDatepickerOpen(false)}
            placeholderText="Select Date"
            isClearable
            disabled={noDeliveryDate}
          />
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              disableRipple
              checked={noDeliveryDate}
              icon={<img src={UncheckedCIcon} alt="unchecked" />}
              checkedIcon={<img src={CheckedCIcon} alt="checked" />}
              className="metadataCheckbox checkedBox"
              onChange={() => {
                if (!noDeliveryDate) {
                  setDateRange([null, null]);
                }
                setNoDeliveryDate(!noDeliveryDate);
              }}
              style={{
                color: '#ED5C2F',
                margin: '0 5px',
                opacity: noDeliveryDate ? '1' : '0.5',
              }}
            />
          }
          label="No Delivery Date"
          sx={{
            marginTop: '12px',
            '& .MuiFormControlLabel-label': {
              color: '#000',
              fontSize: '14px',
              fontWeight: '600',
              opacity: noDeliveryDate ? '1' : '0.5',
            },
          }}
        />
      </Box>
    </OlcFormModal>
  );
};

export default FilterModal;
